/**
 * Whether the sidebar is collapsed — one store, two buttons.
 *
 * The toggle lives in the desktop titlebar (next to the app menu) but the rail
 * it collapses lives in `AppShell`, and the two are **siblings** under
 * `main.tsx` — so the state cannot live in either component. Same pattern as
 * `projectSelectionStore`: the rules in a plain module a test can hold to, a
 * `useSyncExternalStore` pair for React to subscribe with, and every
 * `localStorage` access guarded — a remembered preference is a convenience,
 * and losing it must never cost a render.
 *
 * The titlebar button is the only toggle — it is always there, so the shell
 * carries no opener of its own (owner, 2026-08-24). That makes this store
 * carry both hidden states: `collapsed`, the persisted desktop preference,
 * and `mobileOpen`, the narrow viewport's transient drawer.
 */

const STORAGE_KEY = "bimgo_sidebar_collapsed";

/** Somewhere to read and write strings — `localStorage`, or a stub in a test. */
export interface StringStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStore(): StringStore | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Touching `localStorage` itself throws where site data is blocked.
    return null;
  }
}

/**
 * What a stored value means. Only the exact token this module writes counts as
 * collapsed — anything else (an older shape, a half-written entry, garbage)
 * falls back to the expanded rail, which is the safe default: a rail that is
 * wrongly open costs a click, one that is wrongly gone reads as a broken app.
 */
export function parseCollapsed(raw: string | null): boolean {
  return raw === "1";
}

export interface SidebarStore {
  collapsed(): boolean;
  setCollapsed(next: boolean): void;
  toggle(): void;
  /**
   * The narrow viewport's drawer — **in memory only, never persisted**:
   * reopening the app onto a full-screen scrim because the drawer was open at
   * close is the old web's `isHistoryOpen` bug class, not a preference.
   */
  mobileOpen(): boolean;
  setMobileOpen(next: boolean): void;
  /**
   * The titlebar button's single meaning per viewport: at drawer widths it
   * opens or closes the drawer, at rail widths it flips the persisted
   * preference. The width is an argument, not a `matchMedia` call here, so the
   * rule stays pure and the caller's breakpoint stays the CSS's.
   */
  toggleFor(isNarrow: boolean): void;
  /**
   * Whether a rail exists to toggle at all (#63).
   *
   * The titlebar used to gate its button on there being a session, which was
   * right while the only sessionless screen was login. It is not right any
   * more: the organization gate has a session and no shell, so the button was
   * still drawn and toggled a rail that was not on the page. `AppShell` reports
   * its own presence instead, which is the thing the button actually depends
   * on — and it cannot go stale, because it is set and cleared by the mount
   * that owns the rail.
   */
  shellMounted(): boolean;
  setShellMounted(next: boolean): void;
  /** Returns the unsubscribe, as `useSyncExternalStore` expects. */
  subscribe(listener: () => void): () => void;
}

export function createSidebarStore(store: StringStore | null = browserStore()): SidebarStore {
  let collapsed = parseCollapsed(store?.getItem(STORAGE_KEY) ?? null);
  let mobileOpen = false;
  let shellMounted = false;
  const listeners = new Set<() => void>();
  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  const write = (next: boolean): void => {
    if (next === collapsed) return;
    collapsed = next;
    try {
      store?.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      // Storage refused; the in-memory state still drives this session.
    }
    notify();
  };

  const writeMobile = (next: boolean): void => {
    if (next === mobileOpen) return;
    mobileOpen = next;
    notify();
  };

  return {
    collapsed: () => collapsed,
    setCollapsed: write,
    toggle: () => write(!collapsed),
    mobileOpen: () => mobileOpen,
    setMobileOpen: writeMobile,
    shellMounted: () => shellMounted,
    setShellMounted: (next) => {
      if (next === shellMounted) return;
      shellMounted = next;
      // A rail that is going away takes any open drawer with it, or the scrim
      // outlives the thing it was dimming.
      if (!next) mobileOpen = false;
      notify();
    },
    toggleFor: (isNarrow) => {
      if (isNarrow) writeMobile(!mobileOpen);
      else write(!collapsed);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** The app's one instance — both buttons and the shell read this. */
export const sidebarStore: SidebarStore = createSidebarStore();
